import { TenantConfig, TenantId } from './types';

export * from './types';

/**
 * Registro de tenants implementados. Es `Partial` a propósito: los que todavía no existen (Surco) quedan
 * explícitamente ausentes y, si se intenta arrancar con ellos, la app falla con un error claro.
 *
 * Cada config se carga con `require` dentro de una comparación contra `process.env.VUE_APP_TENANT`: webpack
 * reemplaza la variable por su valor en el build y elimina las ramas muertas, así que el bundle de un tenant no
 * incluye el config, el logo ni el adaptador de API de los demás.
 */
const TENANT_REGISTRY: Partial<Record<TenantId, () => TenantConfig>> = {
    [TenantId.Tasaciones]: () =>
        process.env.VUE_APP_TENANT === TenantId.Tasaciones ? require('./tasaciones/config').tasacionesConfig : null,
    [TenantId.Zurich]: () =>
        process.env.VUE_APP_TENANT === TenantId.Zurich ? require('./zurich/config').zurichConfig : null
};

const isTenantId = (value: unknown): value is TenantId =>
    typeof value === 'string' && (Object.values(TenantId) as string[]).includes(value);

/** Resuelve el `TenantConfig` a partir de un valor de `VUE_APP_TENANT`. Lanza un error si no es válido. */
export const resolveTenant = (rawTenantId: string | undefined): TenantConfig => {
    if (!isTenantId(rawTenantId)) {
        throw new Error(
            `[tenants] VUE_APP_TENANT inválido o ausente: "${rawTenantId}". ` +
                `Valores posibles: ${Object.values(TenantId).join(', ')}.`
        );
    }
    const loadConfig = TENANT_REGISTRY[rawTenantId];
    const config = loadConfig ? loadConfig() : null;
    if (!config) {
        throw new Error(`[tenants] El tenant "${rawTenantId}" todavía no está implementado en este build.`);
    }
    return config;
};

/** Tenant activo de este build (se resuelve una sola vez, al arrancar). */
export const tenant: TenantConfig = resolveTenant(process.env.VUE_APP_TENANT);

/**
 * Acceso al tenant activo fuera de componentes (plugins, router, etc.).
 * Dentro de componentes preferir `@Inject(InjectionKey.Tenant)`.
 */
export const useTenant = (): TenantConfig => tenant;
